import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Eye } from 'lucide-react';

interface VisionData {
  total: number;
  avgLeftEyeScore: number | null;
  avgRightEyeScore: number | null;
  avgColorVisionScore: number | null;
  avgContrastScore: number | null;
  macularHealthDistribution: Array<{ name: string; value: number }> | null;
}

interface VisionAssessmentCardProps {
  data: VisionData | null;
  isLoading: boolean;
}

const MACULAR_COLORS: Record<string, string> = {
  'Normal': 'hsl(142, 76%, 36%)',
  'At Risk': 'hsl(38, 92%, 50%)',
  'Abnormal': 'hsl(0, 84%, 60%)',
  'Unknown': 'hsl(217, 19%, 50%)',
};

const DEFAULT_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

export const VisionAssessmentCard: React.FC<VisionAssessmentCardProps> = ({
  data,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-primary" />
            Vision Assessment Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-primary" />
            Vision Assessment Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            No vision assessment data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const scoreMetrics = [
    { label: 'Avg Left Eye', value: data.avgLeftEyeScore, max: 100, color: 'bg-blue-500' },
    { label: 'Avg Right Eye', value: data.avgRightEyeScore, max: 100, color: 'bg-green-500' },
    { label: 'Avg Color Vision', value: data.avgColorVisionScore, max: 100, color: 'bg-purple-500' },
    { label: 'Avg Contrast', value: data.avgContrastScore, max: 100, color: 'bg-amber-500' },
  ];

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Eye className="h-5 w-5 text-primary" />
          Vision Assessment Insights
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            ({data.total} assessments)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Score Metrics */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-muted-foreground">Average Scores</h4>
            <div className="space-y-3">
              {scoreMetrics.map((metric) => (
                <div key={metric.label} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>{metric.label}</span>
                    <span className="font-medium">
                      {metric.value !== null ? `${metric.value}%` : 'N/A'}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full ${metric.color} transition-all`}
                      style={{ width: `${metric.value ?? 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Macular Health Distribution */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Macular Health Status</h4>
            {data.macularHealthDistribution && data.macularHealthDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={data.macularHealthDistribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={60}
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={false}
                  >
                    {data.macularHealthDistribution.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={MACULAR_COLORS[entry.name] || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[180px] flex items-center justify-center text-muted-foreground">
                No macular health data
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
